import React, { useState, useEffect, useCallback } from 'react';
import { useSuperAdmin } from '../../contexts/SuperAdminContext';
import { useParams, useNavigate } from 'react-router-dom';
import SALayout from './SALayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import {
  ArrowLeft, Building2, Users, CreditCard, Tag, Clock, Save,
  Loader2, Trash2, AlertTriangle, Check, X, Shield, IndianRupee, LogIn, ToggleLeft
} from 'lucide-react';
import { Switch } from '../../components/ui/switch';

const CLEANUP_TYPES = [
  { value: 'time_entries', label: 'Time Entries', desc: 'All tracked time data' },
  { value: 'screenshots', label: 'Screenshots', desc: 'All captured screenshots' },
  { value: 'pay_runs', label: 'Pay Runs & Payslips', desc: 'All payroll data' },
  { value: 'invoices', label: 'Invoices', desc: 'All generated invoices' },
  { value: 'leave_balances', label: 'Leave Data', desc: 'Balances and requests' },
  { value: 'transactions', label: 'Transactions', desc: 'Payment transaction records' },
  { value: 'timesheets', label: 'Timesheets', desc: 'Weekly timesheet data' },
  { value: 'activity_logs', label: 'Activity & Logs', desc: 'Activity, usage, and audit logs' },
  { value: 'notifications', label: 'Notifications', desc: 'All notification records' },
  { value: 'full_wipe', label: 'FULL WIPE', desc: 'Delete ALL data for this org (irreversible)' },
];

export default function SAOrgDetail() {
  const { apiFetch, token } = useSuperAdmin();
  const { orgId } = useParams();
  const navigate = useNavigate();
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [promoInput, setPromoInput] = useState('');
  const [applyingPromo, setApplyingPromo] = useState(false);
  const [cleanupConfirm, setCleanupConfirm] = useState('');
  const [cleanupType, setCleanupType] = useState(null);
  const [cleaning, setCleaning] = useState(false);
  const [impersonating, setImpersonating] = useState(false);
  const [orgFeatures, setOrgFeatures] = useState(null);
  const [togglingInternal, setTogglingInternal] = useState(false);

  // Editable subscription fields
  const [subEdit, setSubEdit] = useState({
    plan: '', plan_tier: '', trial_ends_at: '', is_active: true, seat_limit: 5, price_per_user: 199
  });

  const fetchOrg = useCallback(async () => {
    try {
      const res = await apiFetch(`/organizations/${orgId}`);
      if (res.ok) {
        const data = await res.json();
        setOrg(data);
        const sub = data.subscription || {};
        setSubEdit({
          plan: sub.plan || 'free',
          plan_tier: sub.plan_tier || 'business',
          trial_ends_at: sub.trial_ends_at ? new Date(sub.trial_ends_at).toISOString().split('T')[0] : '',
          is_active: sub.is_active !== false,
          seat_limit: data.workspace_settings?.seat_limit || 5,
          price_per_user: sub.price_per_user || 199
        });
      } else {
        toast.error('Organization not found');
        navigate('/superadmin/organizations');
      }
    } catch {} finally { setLoading(false); }
  }, [apiFetch, orgId, navigate]);

  useEffect(() => { fetchOrg(); }, [fetchOrg]);

  // Fetch org feature flags
  useEffect(() => {
    if (!orgId || !token) return;
    apiFetch(`/feature-flags/org/${orgId}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setOrgFeatures(data); })
      .catch(() => {});
  }, [orgId, token, apiFetch]);

  const handleSaveSub = async () => {
    setSaving(true);
    try {
      const body = { ...subEdit };
      if (!body.trial_ends_at) body.trial_ends_at = null;
      const res = await apiFetch(`/organizations/${orgId}/subscription`, { method: 'PUT', body: JSON.stringify(body) });
      if (res.ok) {
        toast.success('Subscription updated');
        fetchOrg();
      } else toast.error('Failed to update');
    } catch { toast.error('Error'); } finally { setSaving(false); }
  };

  const handleApplyPromo = async () => {
    if (!promoInput.trim()) return;
    setApplyingPromo(true);
    try {
      const res = await apiFetch(`/organizations/${orgId}/apply-promo`, {
        method: 'POST', body: JSON.stringify({ promo_code: promoInput })
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message);
        setPromoInput('');
        fetchOrg();
      } else toast.error(data.detail);
    } catch { toast.error('Failed to apply'); } finally { setApplyingPromo(false); }
  };

  const handleToggleInternal = async () => {
    if (!org) return;
    const next = !org.is_internal;
    const confirmMsg = next
      ? `Mark "${org.name}" as INTERNAL? This waives ALL payment requirements — they get the product for free, lifetime.`
      : `Remove INTERNAL flag from "${org.name}"? Standard billing rules will apply again.`;
    if (!window.confirm(confirmMsg)) return;

    setTogglingInternal(true);
    try {
      const res = await apiFetch(`/organizations/${orgId}/internal`, {
        method: 'PUT',
        body: JSON.stringify({ is_internal: next })
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message || 'Updated');
        fetchOrg();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to update');
      }
    } catch { toast.error('Failed to update internal flag'); }
    finally { setTogglingInternal(false); }
  };

  const handleRemovePromo = async () => {
    try {
      const res = await apiFetch(`/organizations/${orgId}/remove-promo`, { method: 'POST' });
      if (res.ok) { toast.success('Promo removed'); fetchOrg(); }
    } catch { toast.error('Failed'); }
  };

  const handleToggleStatus = async () => {
    try {
      const newStatus = !subEdit.is_active;
      const res = await apiFetch(`/organizations/${orgId}/status`, {
        method: 'PUT', body: JSON.stringify({ is_active: newStatus })
      });
      if (res.ok) {
        toast.success(`Organization ${newStatus ? 'activated' : 'suspended'}`);
        setSubEdit(prev => ({ ...prev, is_active: newStatus }));
        fetchOrg();
      }
    } catch { toast.error('Failed'); }
  };

  const handleImpersonate = async () => {
    setImpersonating(true);
    try {
      const res = await apiFetch(`/organizations/${orgId}/impersonate`, { method: 'POST' });
      const data = await res.json();
      if (res.ok) {
        // Backup SA token
        const saToken = localStorage.getItem('inspirotracker_sa_token');
        if (saToken) localStorage.setItem('inspirotracker_sa_backup', saToken);
        // Store impersonation info
        localStorage.setItem('inspirotracker_impersonation', JSON.stringify({
          orgId: orgId,
          orgName: org.name,
          adminEmail: data.user.email,
          adminName: data.user.name
        }));
        // Set the impersonation token as active auth
        localStorage.setItem('inspirotracker_token', data.token);
        // Redirect to main app
        window.location.href = '/admin/dashboard';
      } else {
        toast.error(data.detail || 'Failed to impersonate');
      }
    } catch { toast.error('Impersonation failed'); } finally { setImpersonating(false); }
  };

  const handleFeatureToggle = async (key, currentEnabled, source) => {
    if (source === 'override') {
      // Already has an override — toggle it
      const res = await apiFetch(`/feature-flags/${key}/orgs/${orgId}`, {
        method: 'PUT', body: JSON.stringify({ enabled: !currentEnabled })
      });
      if (res.ok) {
        setOrgFeatures(prev => ({ ...prev, [key]: { ...prev[key], enabled: !currentEnabled, source: 'override' } }));
        toast.success(`${key} is now ${!currentEnabled ? 'ON' : 'OFF'} for this org`);
      }
    } else {
      // No override yet — create one (opposite of global)
      const res = await apiFetch(`/feature-flags/${key}/orgs/${orgId}`, {
        method: 'PUT', body: JSON.stringify({ enabled: !currentEnabled })
      });
      if (res.ok) {
        setOrgFeatures(prev => ({ ...prev, [key]: { ...prev[key], enabled: !currentEnabled, source: 'override' } }));
        toast.success(`Override set: ${key} is now ${!currentEnabled ? 'ON' : 'OFF'}`);
      }
    }
  };

  const handleRemoveFeatureOverride = async (key, globalDefault) => {
    const res = await apiFetch(`/feature-flags/${key}/orgs/${orgId}`, { method: 'DELETE' });
    if (res.ok) {
      setOrgFeatures(prev => ({ ...prev, [key]: { ...prev[key], enabled: globalDefault, source: 'global' } }));
      toast.success('Override removed');
    }
  };

  const handleCleanup = async () => {
    if (!cleanupType) return;
    if (cleanupType === 'full_wipe' && cleanupConfirm !== org.name) {
      toast.error('Type the organization name to confirm');
      return;
    }
    setCleaning(true);
    try {
      const res = await apiFetch(`/organizations/${orgId}/cleanup`, {
        method: 'POST', body: JSON.stringify({ type: cleanupType })
      });
      const data = await res.json();
      if (res.ok) {
        const counts = Object.entries(data.deleted || {}).map(([k, v]) => `${k}: ${v}`).join(', ');
        toast.success(`Cleanup done. ${counts}`);
        setCleanupType(null);
        setCleanupConfirm('');
      } else toast.error(data.detail);
    } catch { toast.error('Cleanup failed'); } finally { setCleaning(false); }
  };

  if (loading) {
    return <SALayout><div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div></SALayout>;
  }

  if (!org) return null;
  const sub = org.subscription || {};

  return (
    <SALayout>
      <div className="space-y-6" data-testid="sa-org-detail">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/superadmin/organizations')} className="h-8 px-2">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-slate-900">{org.name}</h1>
              <p className="text-xs text-slate-500">Created {new Date(org.created_at).toLocaleDateString()} | {org.users?.length || 0} users</p>
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Button
              size="sm"
              className="h-8 text-xs bg-blue-600 hover:bg-blue-700 text-white"
              onClick={handleImpersonate}
              disabled={impersonating}
              data-testid="sa-impersonate-btn"
            >
              {impersonating ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <LogIn className="w-3 h-3 mr-1" />}
              Login as Admin
            </Button>
            <Button
              variant={subEdit.is_active ? 'outline' : 'default'}
              size="sm"
              className={`h-8 text-xs ${subEdit.is_active ? 'border-red-200 text-red-600 hover:bg-red-50' : 'bg-emerald-600 hover:bg-emerald-700 text-white'}`}
              onClick={handleToggleStatus}
              data-testid="sa-toggle-org-status"
            >
              {subEdit.is_active ? <><X className="w-3 h-3 mr-1" /> Suspend</> : <><Check className="w-3 h-3 mr-1" /> Activate</>}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Spend Summary */}
          <Card className="border-green-200/60 lg:col-span-2" data-testid="sa-org-spend-card">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center flex-shrink-0">
                <IndianRupee className="w-5 h-5 text-green-600" />
              </div>
              <div className="flex-1">
                <p className="text-xs text-slate-500">Total Spend (Lifetime)</p>
                <p className="text-2xl font-bold text-slate-900" data-testid="sa-org-total-spend">
                  ₹{(org.total_spend || 0).toLocaleString('en-IN')}
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs text-slate-500">Paid Transactions</p>
                <p className="text-lg font-semibold text-slate-700" data-testid="sa-org-tx-count">{org.transaction_count || 0}</p>
              </div>
            </CardContent>
          </Card>

          {/* Subscription Control */}
          <Card className="border-slate-200/60" data-testid="sa-subscription-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-blue-600" /> Subscription Control
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Internal Org banner / toggle */}
              <div className={`flex items-start justify-between gap-3 p-3 rounded-lg border ${
                org?.is_internal ? 'bg-emerald-50 border-emerald-300' : 'bg-slate-50 border-slate-200'
              }`} data-testid="sa-internal-org-toggle-row">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <Shield className={`w-4 h-4 ${org?.is_internal ? 'text-emerald-600' : 'text-slate-400'}`} />
                    <p className="text-xs font-semibold text-slate-900">
                      Internal Organization
                    </p>
                    {org?.is_internal && (
                      <Badge variant="default" className="bg-emerald-600 hover:bg-emerald-600 text-[10px]">LIFETIME FREE</Badge>
                    )}
                  </div>
                  <p className="text-[11px] text-slate-500 mt-1">
                    {org?.is_internal
                      ? 'All payments waived. This org bypasses billing & gets the product free forever.'
                      : 'Mark as internal to waive all payment requirements (free, lifetime).'}
                  </p>
                </div>
                <Switch
                  checked={!!org?.is_internal}
                  disabled={togglingInternal}
                  onCheckedChange={handleToggleInternal}
                  data-testid="sa-internal-org-toggle"
                />
              </div>

              {/* Plan summary (read-only insights) */}
              <div className="grid grid-cols-2 gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                <div>
                  <Label className="text-[10px] text-slate-500 uppercase tracking-wide">Next Renewal</Label>
                  <p className="text-sm font-semibold text-slate-900 mt-0.5" data-testid="sa-next-renewal">
                    {sub.current_period_end
                      ? new Date(sub.current_period_end).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                      : (sub.trial_ends_at
                          ? `${new Date(sub.trial_ends_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })} (trial)`
                          : '—')}
                  </p>
                </div>
                <div>
                  <Label className="text-[10px] text-slate-500 uppercase tracking-wide">Last Payment</Label>
                  <p className="text-sm font-semibold text-slate-900 mt-0.5" data-testid="sa-last-payment">
                    {sub.current_period_start
                      ? new Date(sub.current_period_start).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                      : '—'}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Plan Status</Label>
                  <select value={subEdit.plan} onChange={e => setSubEdit(p => ({ ...p, plan: e.target.value }))}
                    className="w-full h-8 px-2 text-xs rounded-lg border border-slate-200 bg-white" data-testid="sa-plan-select">
                    <option value="trial">Trial</option>
                    <option value="active">Active</option>
                    <option value="expired">Expired</option>
                    <option value="cancelled">Cancelled</option>
                    <option value="free">Free</option>
                  </select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Plan Tier</Label>
                  <select value={subEdit.plan_tier} onChange={e => setSubEdit(p => ({ ...p, plan_tier: e.target.value }))}
                    className="w-full h-8 px-2 text-xs rounded-lg border border-slate-200 bg-white" data-testid="sa-plan-tier-select">
                    <option value="individual">Individual</option>
                    <option value="business">Business</option>
                    <option value="premium">Premium</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Trial Ends At</Label>
                  <Input type="date" value={subEdit.trial_ends_at} onChange={e => setSubEdit(p => ({ ...p, trial_ends_at: e.target.value }))}
                    className="h-8 text-xs rounded-lg" data-testid="sa-trial-date" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Seat Limit</Label>
                  <Input type="number" min={1} value={subEdit.seat_limit} onChange={e => setSubEdit(p => ({ ...p, seat_limit: parseInt(e.target.value) || 1 }))}
                    className="h-8 text-xs rounded-lg" data-testid="sa-seat-limit" />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Price Per User (₹/month)</Label>
                <Input type="number" min={0} value={subEdit.price_per_user} onChange={e => setSubEdit(p => ({ ...p, price_per_user: parseInt(e.target.value) || 0 }))}
                  className="h-8 text-xs rounded-lg w-32" data-testid="sa-price-per-user" />
              </div>
              <Button onClick={handleSaveSub} disabled={saving} className="h-8 text-xs rounded-xl" data-testid="sa-save-sub-btn">
                {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
                Save Changes
              </Button>
            </CardContent>
          </Card>

          {/* Promo Code */}
          <Card className="border-slate-200/60" data-testid="sa-promo-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Tag className="w-4 h-4 text-purple-600" /> Promo Code
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {sub.promo_code ? (
                <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg border border-purple-200">
                  <div>
                    <p className="text-sm font-bold text-purple-700">{sub.promo_code}</p>
                    <p className="text-[10px] text-purple-500">
                      Effective: ₹{sub.effective_price_per_user}/user | Expires: {sub.promo_expires_at ? new Date(sub.promo_expires_at).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={handleRemovePromo} className="h-7 text-xs text-red-600 hover:text-red-700 hover:bg-red-50" data-testid="sa-remove-promo">
                    <X className="w-3 h-3 mr-1" /> Remove
                  </Button>
                </div>
              ) : (
                <p className="text-xs text-slate-400">No promo code applied</p>
              )}
              <div className="flex gap-2">
                <Input
                  value={promoInput}
                  onChange={e => setPromoInput(e.target.value)}
                  placeholder="Enter promo code"
                  className="h-8 text-xs rounded-lg flex-1"
                  data-testid="sa-promo-input"
                />
                <Button onClick={handleApplyPromo} disabled={applyingPromo || !promoInput.trim()} className="h-8 text-xs rounded-xl" data-testid="sa-apply-promo-btn">
                  {applyingPromo ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Apply'}
                </Button>
              </div>

              {/* Admins */}
              <div className="pt-3 border-t border-slate-100">
                <p className="text-xs font-medium text-slate-500 mb-2">Org Admins</p>
                <div className="space-y-1.5">
                  {(org.admins || []).map((a, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs">
                      <Shield className="w-3 h-3 text-blue-500" />
                      <span className="text-slate-700">{a.name}</span>
                      <span className="text-slate-400">({a.email})</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Recent Transactions */}
              {org.recent_transactions?.length > 0 && (
                <div className="pt-3 border-t border-slate-100">
                  <p className="text-xs font-medium text-slate-500 mb-2">Recent Transactions</p>
                  <div className="space-y-1.5 max-h-32 overflow-y-auto">
                    {org.recent_transactions.map((t, i) => (
                      <div key={i} className="flex items-center justify-between text-xs">
                        <span className="text-slate-600 truncate max-w-[60%]">{t.description || 'Payment'}</span>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-700">₹{t.amount}</span>
                          <span className={`text-[10px] ${t.status === 'paid' ? 'text-emerald-600' : 'text-amber-600'}`}>{t.status}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Data Cleanup */}
        <Card className="border-red-200/60" data-testid="sa-cleanup-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold flex items-center gap-2 text-red-700">
              <Trash2 className="w-4 h-4" /> Data Cleanup
            </CardTitle>
            <p className="text-xs text-slate-500 mt-1">Permanently delete data for <strong>{org.name}</strong>. This cannot be undone.</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
              {CLEANUP_TYPES.map(ct => (
                <button
                  key={ct.value}
                  onClick={() => { setCleanupType(ct.value); setCleanupConfirm(''); }}
                  className={`p-2.5 rounded-lg border text-left transition-all ${
                    cleanupType === ct.value
                      ? ct.value === 'full_wipe' ? 'border-red-400 bg-red-50 ring-2 ring-red-200' : 'border-blue-400 bg-blue-50 ring-2 ring-blue-200'
                      : 'border-slate-200 hover:border-slate-300 bg-white'
                  }`}
                  data-testid={`sa-cleanup-${ct.value}`}
                >
                  <p className={`text-xs font-semibold ${ct.value === 'full_wipe' ? 'text-red-700' : 'text-slate-700'}`}>{ct.label}</p>
                  <p className="text-[10px] text-slate-400 mt-0.5">{ct.desc}</p>
                </button>
              ))}
            </div>

            {cleanupType && (
              <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
                <div className="flex-1">
                  {cleanupType === 'full_wipe' ? (
                    <div className="space-y-2">
                      <p className="text-xs text-amber-800 font-medium">Type "{org.name}" to confirm full wipe:</p>
                      <Input value={cleanupConfirm} onChange={e => setCleanupConfirm(e.target.value)} placeholder={org.name} className="h-8 text-xs rounded-lg max-w-xs" data-testid="sa-cleanup-confirm-input" />
                    </div>
                  ) : (
                    <p className="text-xs text-amber-800">This will delete all <strong>{CLEANUP_TYPES.find(c => c.value === cleanupType)?.label}</strong> for {org.name}</p>
                  )}
                </div>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleCleanup}
                  disabled={cleaning || (cleanupType === 'full_wipe' && cleanupConfirm !== org.name)}
                  className="h-8 text-xs rounded-xl"
                  data-testid="sa-cleanup-execute-btn"
                >
                  {cleaning ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Trash2 className="w-3 h-3 mr-1" />}
                  Delete
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Feature Flags for this Org */}
        {orgFeatures && (
          <Card className="border-slate-200/60" data-testid="sa-org-features-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <ToggleLeft className="w-4 h-4 text-slate-600" /> Feature Flags
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {Object.entries(orgFeatures).map(([key, val]) => (
                  <div key={key} className="flex items-center justify-between py-2 px-3 rounded-lg bg-slate-50 border border-slate-100" data-testid={`sa-org-ff-${key}`}>
                    <div className="flex-1 min-w-0">
                      <span className="text-xs font-medium text-slate-800">{val.label}</span>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        {val.source === 'override' ? (
                          <span className="text-[10px] font-bold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">OVERRIDE</span>
                        ) : (
                          <span className="text-[10px] font-medium text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">GLOBAL</span>
                        )}
                        <span className={`text-[10px] font-bold ${val.enabled ? 'text-green-600' : 'text-red-500'}`}>
                          {val.enabled ? 'ON' : 'OFF'}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={val.enabled}
                        onCheckedChange={() => handleFeatureToggle(key, val.enabled, val.source)}
                        className="scale-75"
                        data-testid={`sa-org-ff-toggle-${key}`}
                      />
                      {val.source === 'override' && (
                        <button
                          onClick={() => handleRemoveFeatureOverride(key, val.global_default)}
                          className="p-1 rounded hover:bg-slate-200 text-slate-400 hover:text-red-500 transition-colors"
                          title="Remove override (use global)"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Users Table */}
        <Card className="border-slate-200/60">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Users className="w-4 h-4 text-slate-600" /> Members ({org.users?.length || 0})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left px-3 py-2 text-slate-500 font-medium">Name</th>
                    <th className="text-left px-3 py-2 text-slate-500 font-medium">Email</th>
                    <th className="text-left px-3 py-2 text-slate-500 font-medium">Role</th>
                    <th className="text-left px-3 py-2 text-slate-500 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(org.users || []).map(u => (
                    <tr key={u.id} className="border-b border-slate-50">
                      <td className="px-3 py-2 text-slate-800 font-medium">{u.name}</td>
                      <td className="px-3 py-2 text-slate-500">{u.email}</td>
                      <td className="px-3 py-2">
                        <Badge variant="secondary" className={`text-[10px] ${u.role === 'admin' ? 'bg-blue-100 text-blue-700' : u.role === 'manager' ? 'bg-purple-100 text-purple-700' : 'bg-slate-100 text-slate-600'}`}>
                          {u.role}
                        </Badge>
                      </td>
                      <td className="px-3 py-2">
                        <span className={`w-1.5 h-1.5 rounded-full inline-block mr-1 ${u.status === 'active' ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                        <span className="text-slate-500">{u.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </SALayout>
  );
}
